// CompanyForm.js
import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Divider,
  Paper,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Close, Save, Add, Delete } from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";

const industryOptions = [
  "Tecnología",
  "Construcción",
  "Salud",
  "Educación",
  "Finanzas",
  "Retail",
  "Hostelería",
  "Marketing",
  "Consultoría",
  "Otro",
];

const CompanyForm = ({ company, onSave, onCancel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { add, update } = useAuditFirestore("companies");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    name: company?.name || "",
    taxId: company?.taxId || "",
    industry: company?.industry || "",
    website: company?.website || "",
    email: company?.email || "",
    phone: company?.phone || "",
    address: {
      street: company?.address?.street || "",
      city: company?.address?.city || "",
      state: company?.address?.state || "",
      zipCode: company?.address?.zipCode || "",
      country: company?.address?.country || "España",
    },
    files: company?.files || [],
    notes: company?.notes || "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("address.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: { ...prev.address, [field]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (error) setError("");
  };

  // Manejar archivos
  const handleFileChange = (index, field, value) => {
    const newFiles = [...formData.files];
    newFiles[index] = { ...newFiles[index], [field]: value };
    setFormData(prev => ({ ...prev, files: newFiles }));
  };

  const handleAddFile = () => {
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, { name: "", url: "" }]
    }));
  };

  const handleRemoveFile = (index) => {
    const newFiles = formData.files.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, files: newFiles }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      setError("El nombre de la empresa es obligatorio");
      return;
    }

    // Filtrar archivos vacíos
    const validFiles = formData.files.filter(file => file.name.trim() !== "" && file.url.trim() !== "");

    setLoading(true);
    setError("");
    try {
      if (company?.id) {
        await update(company.id, {
          ...formData,
          files: validFiles,
          updatedAt: new Date().toISOString(),
        });
      } else {
        await add({
          ...formData,
          files: validFiles,
          contacts: company?.contacts || [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
      onSave();
    } catch (err) {
      console.error(err);
      setError(err.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme.palette.background.paper,
      height: { xs: 40, sm: 44 },
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.palette.background.paper,
      paddingInline: "4px",
      fontSize: { xs: "0.8rem", sm: "0.875rem" },
    },
  };

  const sectionTitleSx = {
    fontWeight: 700,
    fontSize: { xs: 14, sm: 15 },
    mb: { xs: 1.5, sm: 2 },
    mt: { xs: 4, sm: 5 },
    color: theme.palette.primary.main,
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: { xs: "16px", sm: "24px" },
        border: "1px solid",
        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
        background: theme.palette.background.paper,
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: { xs: 3, sm: 4 },
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" } }}>
            {company?.id ? "Editar empresa" : "Nueva empresa"}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}>
            Completa los datos de la empresa
          </Typography>
        </Box>
        <IconButton
          onClick={onCancel}
          size="small"
          sx={{ 
            background: theme.palette.background.paper, 
            border: "1px solid",
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
          }}
        >
          <Close sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 }, borderRadius: "12px", fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        {/* DATOS PRINCIPALES */}
        <Typography sx={sectionTitleSx}>Datos de la empresa</Typography>

        <Box
          sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, sm: 2.5 }, width: "100%" }}
        >
          <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2.5 }, flexWrap: "wrap" }}>
            <TextField
              fullWidth
              size="small"
              label="Nombre de empresa"
              name="name"
              value={formData.name}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 2, minWidth: { xs: "100%", sm: "300px" } }}
              required
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
            <TextField
              fullWidth
              size="small"
              label="CIF / NIF"
              name="taxId"
              value={formData.taxId}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2.5 }, flexWrap: "wrap" }}>
            <FormControl
              fullWidth
              size="small"
              sx={{ flex: 1, minWidth: { xs: "100%", sm: "260px" } }}
            >
              <InputLabel sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}>Sector</InputLabel>
              <Select
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                label="Sector"
                sx={{ borderRadius: "12px", background: theme.palette.background.paper, height: { xs: 40, sm: 44 }, fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
              >
                <MenuItem value="" sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                  <em>Seleccionar sector...</em>
                </MenuItem>
                {industryOptions.map((opt) => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              label="Sitio web"
              name="website"
              value={formData.website}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "260px" } }}
              placeholder="https://..."
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
          </Box>
        </Box>

        {/* CONTACTO */}
        <Typography sx={sectionTitleSx}>Datos de contacto</Typography>
        <Box
          sx={{ display: "flex", gap: { xs: 1.5, sm: 2.5 }, flexWrap: "wrap", width: "100%" }}
        >
          <TextField
            fullWidth
            size="small"
            type="email"
            label="Email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "250px" } }}
            inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
          />
          <TextField
            fullWidth
            size="small"
            label="Teléfono"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "250px" } }}
            inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
          />
        </Box>

        {/* DIRECCIÓN */}
        <Typography sx={sectionTitleSx}>Dirección</Typography>

        <Box
          sx={{ display: "flex", flexDirection: "column", gap: { xs: 1.5, sm: 2.5 }, width: "100%" }}
        >
          <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2.5 }, flexWrap: "wrap" }}>
            <TextField
              fullWidth
              size="small"
              label="Calle"
              name="address.street"
              value={formData.address.street}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "300px" } }}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Ciudad"
              name="address.city"
              value={formData.address.city}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "300px" } }}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
          </Box>

          <Box sx={{ display: "flex", gap: { xs: 1.5, sm: 2.5 }, flexWrap: "wrap" }}>
            <TextField
              fullWidth
              size="small"
              label="Provincia"
              name="address.state"
              value={formData.address.state}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
            <TextField
              fullWidth
              size="small"
              label="Código postal"
              name="address.zipCode"
              value={formData.address.zipCode}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
            <TextField
              fullWidth
              size="small"
              label="País"
              name="address.country"
              value={formData.address.country}
              onChange={handleChange}
              sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
              inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
            />
          </Box>
        </Box>

        {/* ARCHIVOS */}
        <Typography sx={sectionTitleSx}>Archivos</Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, width: "100%" }}>
          {formData.files.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                gap: { xs: 1, sm: 1.5 },
                alignItems: "center",
                width: "100%",
                flexWrap: { xs: "wrap", sm: "nowrap" },
              }}
            >
              <TextField
                size="small"
                label="Nombre del archivo"
                value={file.name}
                onChange={(e) => handleFileChange(index, "name", e.target.value)}
                sx={{ ...fieldSx, flex: 1, minWidth: { xs: "100%", sm: "200px" } }}
                inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
              />
              <TextField
                size="small"
                label="Enlace (URL)"
                value={file.url}
                onChange={(e) => handleFileChange(index, "url", e.target.value)}
                sx={{ ...fieldSx, flex: 2, minWidth: { xs: "100%", sm: "300px" } }}
                inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
              />
              <IconButton
                onClick={() => handleRemoveFile(index)}
                size="small"
                sx={{
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: theme.palette.error.main,
                  color: theme.palette.error.main,
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: theme.palette.error.main,
                    color: "white",
                  },
                }}
              >
                <Delete sx={{ fontSize: { xs: 18, sm: 20 } }} />
              </IconButton>
            </Box>
          ))}
          
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={handleAddFile}
            sx={{
              borderRadius: "12px",
              height: { xs: 40, sm: 44 },
              textTransform: "none",
              fontSize: { xs: "0.8rem", sm: "0.85rem" },
              alignSelf: "flex-start",
              mt: 1,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              '&:hover': {
                borderColor: theme.palette.primary.dark,
                backgroundColor: theme.palette.mode === 'dark' 
                  ? 'rgba(0, 175, 163, 0.1)' 
                  : 'rgba(0, 95, 101, 0.05)',
              },
            }}
          >
            Añadir archivo
          </Button>
        </Box>

        {/* NOTAS */}
        <Typography sx={sectionTitleSx}>Notas internas</Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Notas"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          inputProps={{ style: { fontSize: { xs: "0.8rem", sm: "0.875rem" } } }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              background: theme.palette.background.paper,
            },
          }}
        />

        {/* FOOTER */}
        <Divider sx={{ mt: { xs: 4, sm: 5 }, mb: { xs: 2, sm: 3 } }} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            onClick={onCancel}
            sx={{ borderRadius: "12px", px: { xs: 2, sm: 3 }, height: { xs: 40, sm: 44 }, textTransform: "none", fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
            fullWidth={{ xs: true, sm: false }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={<Save />}
            sx={{ borderRadius: "12px", px: { xs: 2, sm: 3 }, height: { xs: 40, sm: 44 }, boxShadow: "none", textTransform: "none", fontSize: { xs: "0.8rem", sm: "0.85rem" } }}
            fullWidth={{ xs: true, sm: false }}
          >
            {loading
              ? "Guardando..."
              : company?.id
                ? "Actualizar"
                : "Crear empresa"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default CompanyForm;